import React, { useState, useEffect, useMemo } from 'react';
import { getAllNews } from '../../services/newsServices';
import { getAllEvents } from '../../services/eventsServices';
import { Box, Paper, Typography } from '@mui/material';
import { useAuth } from '../../context/authContext';
import EventIcon from '@mui/icons-material/Event';

// Componentes
import NewsList from '../../components/news/List';
import NewsFilters from '../../components/news/NewsFilter';
import EventsSidebar from '../../components/news/EventsSideBar';
import GradientHeader from '../../components/common/GradientHeader';
import { PageLoader } from '../../components/loader';

// Utilidades y hooks
import { dateUtils } from '../../utils/dateUtils';
import { useNewsFilters } from '../../hooks/useNewsFilters';

const News = () => {
    const [news, setNews] = useState([]);
    const [countPendingNews, setCountPendingNews] = useState(0);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { user } = useAuth();
    
    // Usar el hook personalizado para filtros
    const { 
        filters, 
        setFilters, 
        filteredNews, 
        categories,
        defaultFilters 
    } = useNewsFilters(news);

    const loadNews = async () => {
        try {
            setLoading(true);
            const data = await getAllNews();
            // Ordenar por fecha al cargar
            const sortedData = data.sort((a, b) => {
                const dateA = dateUtils.normalizeDate(b.creationDate);
                const dateB = dateUtils.normalizeDate(a.creationDate);
                return dateA - dateB;
            });
            setNews(sortedData);
        } catch (error) {
            setError("Hubo un error al cargar las noticias");
        } finally {
            setLoading(false);
        }
    };

    const loadEvents = async () => {
        try {
            setLoading(true);
            const data = await getAllEvents();
            setEvents(data);
        } catch (error) {
            setError("Hubo un error al cargar las noticias");
        } finally {
            setLoading(false);
        }
    };      

    useEffect(() => {
        loadNews();
        loadEvents();
    }, []);

    useEffect(() => {
        setCountPendingNews(news.filter(n => n.status === 'pending').length);
    }, [news]);
    
    // Calcular los eventos próximos usando useMemo para evitar recálculos
    const upcomingEvents = useMemo(() => {
        const today = dateUtils.normalizeDate(new Date());
        return events
            .filter(event => dateUtils.normalizeDate(event.date) >= today)
            .sort((a, b) => dateUtils.normalizeDate(a.date) - dateUtils.normalizeDate(b.date));
    }, [events]);

    if (loading) return <PageLoader message="Cargando noticias..." />;
    if (error) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
            <Typography color="error" variant="h6">{error}</Typography>
        </Box>
    );

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%)', 
            py: 4,
            position: 'relative'
        }}>
            <Box sx={{ 
                maxWidth: 'xl', 
                mx: 'auto', 
                px: { xs: 2, md: 4 },
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 4
            }}>
                {/* Columna principal - Noticias */}
                <Box sx={{ 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3
                }}>
                    {/* Barra de herramientas */}
                    <Paper
                        elevation={2}
                        sx={{
                            borderRadius: '12px',
                            p: 3,
                            background: 'white',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        {/* Título centrado o a la izquierda */}
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                color: '#0488AF',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                        <EventIcon fontSize="small" />
                            Filtros y Acciones
                        </Typography>

                        {/* Contenedor de filtros y acciones */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                justifyContent: 'space-between',
                                alignItems: 'flex-end',
                                gap: 2,
                                flexWrap: 'wrap',
                            }}
                            >
                            {/* Filtros */}
                            <Box sx={{ flex: 1, minWidth: 280 }}>
                                <NewsFilters
                                filters={filters}
                                setFilters={setFilters}
                                categories={categories}
                                defaultFilters={defaultFilters}
                                user={user}
                                countPendingNews={countPendingNews}
                                />
                            </Box>
                        </Box>
                    </Paper>



                    {/* Lista de Noticias */}
                    <Paper elevation={2} sx={{ 
                        borderRadius: '12px',
                        overflow: 'hidden',
                        background: 'white',
                        flex: 1
                    }}>
                        <GradientHeader title="Noticias de la Comunidad" icon="📰" />
                        <Box sx={{ p: 3 }}>
                            {loading ? (
                                <PageLoader message="Cargando noticias..." />
                            ) : (
                                <NewsList filteredNews={filteredNews} onUpdate={loadNews} />
                            )}
                        </Box>
                    </Paper>
                </Box>

                {/* Columna lateral - Eventos (sticky) */}
                <Box
                    sx={{
                        width: { md: 350 },
                        position: 'sticky',
                        top: 20,
                        alignSelf: 'flex-start',
                        height: 'calc(100vh - 40px)',
                        overflowY: 'auto',
                        pr: 1
                    }}
                    >
                    <EventsSidebar events={upcomingEvents} />
                    </Box>

            </Box>
        </Box>
    );
};

export default News;